import { ChangeEventHandler, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useSetAtom } from "jotai";
import { WorkflowService } from "@/generated";
import { SpotToast } from "@/components/SpotToast";
import { useSpotUser } from "@/utils/spotUser";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { importCsvFileAtom } from "@/store/ui-atoms";
import {
  areValidFileTypes,
  FileTypes,
  getAcceptAttributeValue,
  getMaxFileSize,
  isSpreadsheet,
} from "@/utils/mimetypes";
import { withinImportLimit } from "@/utils/csv";

// `onNavigate` runs right before the imported tool's page is opened.
export function useImportTool({
  onNavigate,
}: { onNavigate?: () => void } = {}) {
  const router = useRouter();
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const setImportCsvFile = useSetAtom(importCsvFileAtom);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const importableFileTypes = [FileTypes.JSON, FileTypes.CSV, FileTypes.XLSX];

  const importWorkflowMutation = useMutation({
    mutationFn: async (file: File) => {
      const fileContent = await file.text();

      return WorkflowService.importWorkflow({
        path: { group_id: spotGroup.id },
        body: {
          definition: fileContent,
        },
      });
    },
    onMutate: () => {
      const toastId = SpotToast.loading("Importing the provided file...");
      return { toastId };
    },
    onSuccess: (response: any, _variables, context) => {
      SpotToast.success(
        "Imported successfully",
        undefined,
        undefined,
        context?.toastId
      );
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKFLOWS] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKSHOP_RECENT] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      const items = response.data.filter((item: any) => {
        return (
          item.type === "workflow" ||
          item.type === "assistant" ||
          item.type === "kb"
        );
      });
      if (items.length === 0) {
        return;
      }
      const item = items[0];
      onNavigate?.();

      if (item.type === "assistant") {
        router.push(`/assistant/${item.reference}`);
      } else if (item.type === "kb") {
        router.push(`/data-hub/${item.reference}`);
      } else {
        router.push(`/flows/${item.reference}`);
      }
    },
    onError: (error: any, _variables, context) => {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const errorMessage =
        axiosError?.response?.data?.detail ||
        "There was an error importing your file. Please check the file format and try again.";
      SpotToast.error(errorMessage, undefined, undefined, context?.toastId);
    },
  });

  const isImporting = importWorkflowMutation.status === "pending";

  const handleFileUpload = (files: FileList) => {
    const file = files[0];
    if (!file) return;

    // A spreadsheet can only mean a table; anything else is a .nx bundle of
    // flows, agents and knowledge bases. Routing on the file rather than on
    // the open tab means either can be imported from anywhere.
    if (isSpreadsheet(file.name)) {
      if (withinImportLimit(file)) setImportCsvFile(file);
      return;
    }

    if (!file.name.endsWith(".nx")) {
      SpotToast.error("Please select a .nx, .csv or .xlsx file");
      return;
    }

    importWorkflowMutation.mutate(file);
  };

  const processFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (!areValidFileTypes(files, importableFileTypes)) {
      SpotToast.error("Invalid file format");
      return;
    }

    if (files.length !== 1) {
      SpotToast.error("You can only upload one file.");
      return;
    }

    const MAX_FILE_SIZE = getMaxFileSize();

    const hasFilesTooBig = Array.from(files).some(
      (file) => file.size > MAX_FILE_SIZE
    );
    if (hasFilesTooBig) {
      SpotToast.error(
        `File size exceeds the ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`
      );
      return;
    }

    handleFileUpload(files);
  };

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    processFiles(event.target.files);
    event.target.value = "";
  };

  const fileInput = (
    <input
      className="hidden"
      type="file"
      ref={inputRef}
      disabled={isImporting}
      onChange={handleOnChange}
      multiple={false}
      accept={getAcceptAttributeValue(importableFileTypes)}
    />
  );

  return {
    openFilePicker: () => inputRef.current?.click(),
    isImporting,
    fileInput,
  };
}
